/* eslint-disable jira/filename-case */

import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type { RouteResource } from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/import-whitelist
import { announcementBannerSettingsV4Resource } from '@jira/admin-pages__system-announcement-banner-resources/src/useAnnouncementBannerSettingsV4Resource.tsx';
import { getConsentBannerUserPreferencesResource } from '@jira/router-resources__cookie-consent/src/useConsentBannerUserPreferencesResource.tsx';
import { optionalIncomingMailServerExpiryStatusResource } from '@jira/router-resources__incoming-mail-server-expiry-status/src/optionalIncomingMailServerExpiryStatusResource.tsx';
import { incomingMailServerExpiryStatusResource } from '@jira/router-resources__incoming-mail-server-expiry-status/src/useIncomingMailExpiryStatus.tsx';
import { rfoAnnouncementBannerResource } from '@jira/router-resources__rfo-announcement-banner/src/useRfoAnnouncementBannerResource.tsx';
import {
	browserOnlyResourceWithCondition,
	resourceWithCondition,
} from '@jira/router-resources__utils/src/resourceWithCondition.tsx';

// Kept at module scope so the `getRoutes` duplicate removal, which relies on instance identity,
// still works when `getBannerResources` is spread into more than one resource list.
const conditionalRfoAnnouncementBannerResource = resourceWithCondition(
	() => fg('jira_rfo_announcement_banner'),
	rfoAnnouncementBannerResource,
);

// The demotion fork must wrap the browser-only wrappers: their spread would evaluate its getters at module scope.
const conditionalIncomingMailServerExpiryStatusResource = resourceWithCondition(
	() => fg('jira_ssr_demote_optional_critical_resources'),
	browserOnlyResourceWithCondition(
		() => expVal('jira_mail_expiry_client_only', 'isEnabled', false),
		optionalIncomingMailServerExpiryStatusResource,
	),
	browserOnlyResourceWithCondition(
		() => expVal('jira_mail_expiry_client_only', 'isEnabled', false),
		incomingMailServerExpiryStatusResource,
	),
);

/**
 * Data for various banners that may or may not appear on page load. Eventually these should all disappear in to one of two places:
 *
 * 1. A top-level Jira SPA query, or
 * 2. A route-specific resource that gets rendered via a portal in to the banner location in page layout.
 *
 * @see `src/packages/platform/page-container-v2/src/common/ui/banner`
 */
export const getBannerResources = (): RouteResource[] => [
	getConsentBannerUserPreferencesResource(),
	announcementBannerSettingsV4Resource,
	conditionalRfoAnnouncementBannerResource,
	conditionalIncomingMailServerExpiryStatusResource,
];
